<template>
  <div>
    <PageHeader :breadcrumRoutes="routes_pageheader" title="Empleados" />

    <a-card>
      <a-row align="middle" justify="space-between">
        <a-typography-title :level="4" class="text-gray-8 mb-0">
          Lista de empleados
        </a-typography-title>

        <a-button type="primary" @click="showDownloadStockModal">
          Descargar
        </a-button>
        <a-modal
          v-model:visible="downloadStockModal"
          title="Descargar stock de empleados"
          :ok-button-props="{ disabled: !selectedCompany }"
          @ok="handleDownloadStock"
        >
          <MainFilter :multipleCompanies="false" />

          <a-button
            v-if="selectedCompany"
            :type="showAdvancedFilters ? 'primary' : 'default'"
            class="mt-3"
            @click="showAdvancedFilters = !showAdvancedFilters"
          >
            {{
              showAdvancedFilters
                ? 'Ocultar filtros'
                : 'Agregar filtros (opcional)'
            }}
          </a-button>

          <div v-if="showAdvancedFilters" class="mt-3">
            <a-row align="middle" justify="space-between" class="mb-2">
              <a-col>
                <a-typography-text class="fw-semibold fs-6">
                  Pólizas
                </a-typography-text>
              </a-col>
              <a-col>
                <a-checkbox-group v-model:value="policyStatusFilter">
                  <a-checkbox value="Vigente">Vigentes</a-checkbox>
                  <a-checkbox value="No vigente">No vigentes</a-checkbox>
                </a-checkbox-group>
              </a-col>
            </a-row>
            <a-select
              v-model:value="selectedPolicyIds"
              mode="multiple"
              :options="policyOptions"
              placeholder="Seleccionar pólizas"
              style="width: 100%"
            />
            <a-typography-text type="secondary" class="d-block mb-3">
              Vacío = todas las pólizas
            </a-typography-text>

            <a-typography-text class="d-block">Vigente al</a-typography-text>
            <a-date-picker
              v-model:value="activeDate"
              format="DD-MM-YYYY"
              placeholder="Selecciona una fecha"
              allow-clear
              style="width: 100%"
            />
            <a-typography-text type="secondary" class="d-block">
              Vacío = sin filtro de fecha
            </a-typography-text>
          </div>
        </a-modal>
      </a-row>

      <EmployeesTable class="mt-3" />
    </a-card>
  </div>
</template>

<script setup>
import PageHeader from '@/components/page-header.vue';
import EmployeesTable from '@/components/rrhh/employees-table.vue';
import MainFilter from '@/components/dashboard/select-filter.vue';
import { SELECTED_COMPANY, DOWNLOAD_EXCEL_STOCK } from '@/store/types';
import {
  FETCH_COMPANY_POLICIES,
  GET_COMPANY_POLICIES
} from '@/store/clients/policies/types';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { ref, computed, watch } from 'vue';
import notification from '@/utils/notifications';
import { validateToken } from '@/network/interceptors/validateToken';

const store = useStore();
const router = useRouter();

const routes_pageheader = [
  {
    breadcrumbName: 'Empleados'
  }
];

const downloadStockModal = ref(false);
const showDownloadStockModal = () => {
  downloadStockModal.value = true;
};

const selectedCompany = computed(() => {
  const companyId = store.getters[SELECTED_COMPANY][0];
  return Array.isArray(companyId) ? undefined : companyId;
});

const showAdvancedFilters = ref(false);
const selectedPolicyIds = ref([]);
const activeDate = ref(null);
const policyStatusFilter = ref(['Vigente', 'No vigente']);

const policyOptions = computed(() =>
  store.getters[GET_COMPANY_POLICIES]
    .filter((policy) => policyStatusFilter.value.includes(policy.status))
    .map((policy) => ({
      label: `${policy.policyNumber} (${policy.status})`,
      value: policy.id
    }))
);

watch(
  selectedCompany,
  (companyId) => {
    showAdvancedFilters.value = false;
    selectedPolicyIds.value = [];
    activeDate.value = null;
    policyStatusFilter.value = ['Vigente', 'No vigente'];

    if (companyId) {
      store.dispatch(FETCH_COMPANY_POLICIES, `?companyId=${companyId}`);
    }
  },
  { immediate: true }
);

const handleDownloadStock = async () => {
  if (!selectedCompany.value) {
    notification('error', 'Error', 'Debe seleccionar una compañía.');
    return;
  }

  const isValid = await validateToken();
  if (!isValid) {
    router.push({ name: 'Login' });
    return;
  }

  const url = await store.dispatch(DOWNLOAD_EXCEL_STOCK, {
    companyId: selectedCompany.value,
    policyIds: selectedPolicyIds.value.length
      ? selectedPolicyIds.value
      : undefined,
    activeDate: activeDate.value ? activeDate.value.format('YYYY-MM-DD') : undefined
  });

  const link = document.createElement('a');
  link.href = url;
  link.click();
};
</script>
